import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { connect } from "react-redux";

import { useGetPartData, usePutPartData } from "../../hooks";
import { getUserLocation } from "../../redux/auth";
import { HubHookFormWrapper, PageContent } from "../../components";

import PartDetailsTitle from "./PartDetailsTitle";
import { PART_SECTIONS, UpdateMapper } from "./helper";
import PartDetailsMenu from "./PartDetailsMenu";
import { StyledCardAndMenuStack, StyledDisplayStack } from "./styles";
import PartDetailsSkeleton from "./PartDetailsSkeleton";

const getFormSx = (isReady) => ({
  height: "100%",
  overflow: "hidden",
  ...(!isReady && {
    position: "absolute",
    visibility: "hidden",
    pointerEvents: "none",
  }),
});

export const PartDetails = ({ defaultLocation = 1 }) => {
  const { id } = useParams();
  const [value, setValue] = useState("ROUTING");
  const [locationId, setLocationId] = useState(defaultLocation);
  const [ready, setReady] = useState(false);
  const formRef = useRef(null);

  const { data } = useGetPartData({ id, locationId });
  const { mutate: putPartData } = usePutPartData();

  const dataLoaded = !!data;

  useEffect(() => {
    setLocationId(defaultLocation);
  }, [id, defaultLocation]);

  useEffect(() => {
    setReady(false);
  }, [id, locationId]);

  useEffect(() => {
    if (!dataLoaded || ready) return;
    const frame = requestAnimationFrame(() => {
      requestAnimationFrame(() => setReady(true));
    });
    return () => cancelAnimationFrame(frame);
  }, [dataLoaded, ready]);

  const formDefaultValues = React.useMemo(() => {
    if (!data) return undefined;
    return {
      locationId,
      details: data?.details,
      instructions: data?.instructions,
    };
  }, [data, locationId]);

  const tabProps = { value, setValue };
  const ActiveTab = PART_SECTIONS[value];

  const handleSubmit = useCallback(
    (formData) => {
      if (value === "ROUTING") {
        const payload = UpdateMapper(formData, id);
        putPartData(payload);
      }
    },
    [value, id, putPartData],
  );

  return (
    <PageContent
      disablePadding
      isLoading={!ready}
      skeleton={<PartDetailsSkeleton />}
    >
      {dataLoaded && (
        <HubHookFormWrapper
          onSubmit={handleSubmit}
          sx={getFormSx(ready)}
          defaultValues={formDefaultValues}
          ref={formRef}
        >
          <PartDetailsTitle id={id} />
          <StyledCardAndMenuStack>
            <PartDetailsMenu {...tabProps} />
            <StyledDisplayStack>
              {!!ActiveTab && (
                <ActiveTab
                  id={id}
                  locationId={locationId}
                  setLocationId={setLocationId}
                />
              )}
            </StyledDisplayStack>
          </StyledCardAndMenuStack>
        </HubHookFormWrapper>
      )}
    </PageContent>
  );
};

const mapStateToProps = (state) => ({
  defaultLocation: getUserLocation(state),
});

export const PartDetailsPage = connect(mapStateToProps)(PartDetails);

export default PartDetailsPage;
